import { useState, useRef, useEffect, ReactNode } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from '@/lib/utils';
import { LucideIcon } from 'lucide-react';

export interface TabConfig {
  id: string;
  label: string;
  icon: LucideIcon;
  content: ReactNode;
}

interface LayoutPageTabsProps {
  tabs: TabConfig[];
  defaultTab?: string;
  className?: string;
  onTabChange?: (tabId: string) => void;
  onTabClick?: (tabId: string) => void;
}

export function LayoutPageTabs({ tabs, defaultTab, className, onTabChange, onTabClick }: LayoutPageTabsProps) {
  const [activeTab, setActiveTab] = useState(defaultTab || tabs[0]?.id || "");
  const tabsContainerRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<{ [key: string]: HTMLButtonElement | null }>({});

  useEffect(() => {
    if (defaultTab) {
      setActiveTab(defaultTab);
    }
  }, [defaultTab]);

  // Custom class for active tab to remove any possible border and connect to content
  const activeTabClass = "data-[state=active]:bg-background data-[state=active]:shadow-none data-[state=active]:border-b-background data-[state=active]:border-b-2";

  // Auto-scroll active tab into view (responsive design from HubAdmin)
  useEffect(() => {
    const activeTabElement = tabRefs.current[activeTab];
    const container = tabsContainerRef.current;
    
    if (activeTabElement && container) {
      const containerRect = container.getBoundingClientRect();
      const tabRect = activeTabElement.getBoundingClientRect();
      
      // Check if tab is partially or fully outside the container
      const isOutOfView = 
        tabRect.left < containerRect.left || 
        tabRect.right > containerRect.right;
      
      if (isOutOfView) {
        // Calculate the scroll position to center the tab
        const containerWidth = container.clientWidth;
        const tabWidth = activeTabElement.offsetWidth;
        const tabOffsetLeft = activeTabElement.offsetLeft;
        
        // Center the tab in the container
        const scrollLeft = tabOffsetLeft - (containerWidth - tabWidth) / 2;
        
        // Smooth scroll to the calculated position
        container.scrollTo({
          left: Math.max(0, scrollLeft), // Ensure we don't scroll before the start
          behavior: 'smooth'
        });
      }
    }
  }, [activeTab]);

  return (
    <div className={cn("mb-16", className)}>
      <Tabs
        value={activeTab}
        onValueChange={(tabId) => {
          setActiveTab(tabId);
          onTabChange?.(tabId);
        }}
        className="!mb-0 !pb-0"
      >
        <div className="bg-muted w-full px-2 pt-2 border-b-0">
          <div ref={tabsContainerRef} className="overflow-x-auto scrollbar-hide">
            <TabsList className="bg-transparent relative z-10 !mb-0 !pb-0 shadow-none border-b-0 border-none flex-nowrap min-w-fit">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <TabsTrigger 
                    key={tab.id}
                    ref={(el) => tabRefs.current[tab.id] = el}
                    className={cn("rounded-b-none border-b-2 border-b-transparent", activeTabClass)}
                    value={tab.id}
                    onClick={() => onTabClick?.(tab.id)}
                  >
                    <div className="flex items-center gap-2 px-3 sm:px-4 md:px-6">
                      <Icon size={16} className="flex-shrink-0" />
                      <span className="whitespace-nowrap">{tab.label}</span>
                    </div>
                  </TabsTrigger>
                );
              })}
            </TabsList>
          </div>
        </div>
        
        <div className="bg-background p-4 border-0 border-t-0 relative -mt-[1px]">
          {tabs.map((tab) => (
            <TabsContent key={tab.id} value={tab.id} className="mt-0 border-0">
              {tab.content}
            </TabsContent>
          ))}
        </div>
      </Tabs>
    </div>
  );
}